import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import Breadcrumbs from '../../components/shared/Breadcrumbs';
import AffiliateBanner from '../../components/shared/AffiliateBanner';
import CTABox from '../../components/shared/CTABox';
import TableOfContents from '../../components/shared/TableOfContents';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const LLCWyomingPage = () => {
  const tocItems = [
    { id: 'warum-wyoming', title: 'Warum Wyoming?', level: 2 },
    { id: 'vorteile', title: 'Vorteile einer Wyoming LLC', level: 2 },
    { id: 'kosten', title: 'Kosten & Gebühren', level: 2 },
    { id: 'schritt-fuer-schritt', title: 'Schritt-für-Schritt Anleitung', level: 2 },
    { id: 'faq', title: 'Häufig gestellte Fragen', level: 2 },
  ];

  const faqs = [
    {
      question: 'Muss ich in Wyoming wohnen, um dort eine LLC zu gründen?',
      answer: 'Nein. Sie können eine Wyoming LLC von überall auf der Welt gründen. Sie benötigen lediglich einen Registered Agent mit physischer Adresse in Wyoming. Dies ist ein gesetzliches Erfordernis, das von Services wie Northwest Registered Agent übernommen wird.'
    },
    {
      question: 'Was kostet eine Wyoming LLC insgesamt?',
      answer: 'Die Gesamtkosten im ersten Jahr betragen typischerweise: Staatsgebühr $100 (einmalig), Gründungsservice ca. $39, Registered Agent $125/Jahr (oft 1. Jahr gratis bei Gründung). Jährlich: Annual Report $60 + Registered Agent. Gesamtkosten erstes Jahr: ca. $140-165.'
    },
    {
      question: 'Wie privat ist eine Wyoming LLC wirklich?',
      answer: 'Wyoming bietet einen der stärksten Datenschutzmechanismen in den USA. Die Namen der LLC-Mitglieder (Members) müssen nicht beim Staat eingereicht werden — nur der Registered Agent und der Organizer erscheinen in öffentlichen Unterlagen. Mit einem guten Registered Agent wie Northwest bleibt Ihr Name vollständig privat.'
    },
    {
      question: 'Wie lange dauert die Gründung einer Wyoming LLC?',
      answer: 'Wyoming ist bekannt für schnelle Bearbeitungszeiten. Standard-Einreichungen werden oft innerhalb von 1-2 Werktagen bearbeitet. Mit Northwest dauert der gesamte Prozess von der Anmeldung bis zur fertigen LLC typischerweise 24-48 Stunden.'
    },
    {
      question: 'Muss ich Steuern in Wyoming zahlen?',
      answer: 'Wyoming erhebt keine Staatseinkommensteuer und keine Franchise Tax. Als LLC-Inhaber zahlen Sie nur die föderalen US-Steuern (wenn anwendbar). Die genauen Steuerpflichten hängen von Ihrer Situation ab — konsultieren Sie einen Steuerberater für individuelle Beratung.'
    },
  ];

  return (
    <div className="py-12" data-testid="llc-wyoming-page">
      <div className="container-wide">
        <Breadcrumbs items={[
          { label: 'Ratgeber', href: '/ratgeber' },
          { label: 'LLC in Wyoming gründen', href: '/ratgeber/llc-wyoming-gruenden' }
        ]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              <header className="mb-12">
                <h1 className="mb-6">LLC in Wyoming gründen — Der ultimative Leitfaden 2026</h1>
                <p className="text-xl text-slate-600 mb-6">
                  Wyoming ist der #1 Bundesstaat für LLC-Gründungen — und das aus gutem Grund. 
                  Keine Staatseinkommensteuer, starker Datenschutz und die niedrigsten Jahresgebühren 
                  machen Wyoming zur idealen Wahl für internationale Unternehmer.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 10 Min.</span>
                </div>
              </header>

              {/* Warum Wyoming */}
              <section id="warum-wyoming" className="mb-16">
                <h2>Warum Wyoming?</h2>
                <p>
                  Wyoming war der erste US-Bundesstaat, der die LLC als Unternehmensform einführte 
                  (1977) — und ist bis heute der Goldstandard für LLC-Gründungen. Der Staat hat über 
                  Jahrzehnte ein extrem unternehmensfreundliches Rechtssystem entwickelt.
                </p>
                <p>
                  Für internationale Unternehmer bietet Wyoming eine einzigartige Kombination aus 
                  Datenschutz, niedrigen Kosten und minimaler Bürokratie. Es gibt einen Grund, 
                  warum mehr LLCs in Wyoming registriert sind als der Staat Einwohner hat.
                </p>
              </section>

              {/* Vorteile */}
              <section id="vorteile" className="mb-16">
                <h2>Vorteile einer Wyoming LLC</h2>
                
                <div className="space-y-6">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Keine Staatseinkommensteuer
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Wyoming erhebt keine Einkommensteuer auf Unternehmens- oder Privatebene. 
                      Das bedeutet keine zusätzliche Steuerlast durch den Staat.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Starker Datenschutz
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Die Namen der LLC-Mitglieder müssen nicht öffentlich gemacht werden. 
                      Mit dem richtigen Registered Agent bleibt Ihre Identität geschützt.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Niedrigste Jahresgebühren
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Der Annual Report kostet nur $60/Jahr — einer der niedrigsten Werte aller 
                      US-Bundesstaaten. Keine Franchise Tax, keine versteckten Gebühren.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Starker Vermögensschutz
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Wyoming hat fortschrittliche LLC-Gesetze mit starkem Schutz gegen Gläubigerzugriff 
                      (Charging Order Protection).
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Schnelle Bearbeitung
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Wyoming Secretary of State bearbeitet LLC-Anträge oft innerhalb von 24 Stunden — 
                      einer der schnellsten Prozesse in den USA.
                    </p>
                  </div>
                </div>
              </section>

              {/* Kosten */}
              <section id="kosten" className="mb-16">
                <h2>Kosten & Gebühren</h2>

                <div className="card-flat p-6 mb-6">
                  <h4 className="font-heading font-bold text-navy-900 mb-4">Einmalige Kosten</h4>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex justify-between">
                      <span>Wyoming State Filing Fee</span>
                      <span className="font-medium">$100</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Northwest Gründungsservice</span>
                      <span className="font-medium">$39</span>
                    </li>
                    <li className="flex justify-between border-t pt-2 mt-2">
                      <span className="font-bold">Gesamt (einmalig)</span>
                      <span className="font-bold text-teal-600">$139</span>
                    </li>
                  </ul>
                </div>

                <div className="card-flat p-6">
                  <h4 className="font-heading font-bold text-navy-900 mb-4">Jährliche Kosten (ab Jahr 2)</h4>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex justify-between">
                      <span>Wyoming Annual Report</span>
                      <span className="font-medium">$60</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Registered Agent (Northwest)</span>
                      <span className="font-medium">$125</span>
                    </li>
                    <li className="flex justify-between border-t pt-2 mt-2">
                      <span className="font-bold">Gesamt (jährlich)</span>
                      <span className="font-bold text-teal-600">$185</span>
                    </li>
                  </ul>
                </div>

                <p className="mt-6 text-sm text-slate-600">
                  <strong>Hinweis:</strong> Das erste Jahr Registered Agent ist bei Gründung über 
                  Northwest kostenlos inklusive, was die Gesamtkosten im ersten Jahr auf ca. $139 reduziert.
                </p>
              </section>

              {/* Schritt für Schritt */}
              <section id="schritt-fuer-schritt" className="mb-16">
                <h2>Schritt-für-Schritt: Wyoming LLC gründen</h2>

                <div className="space-y-6">
                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        1
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Namen wählen und prüfen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Ihr LLC-Name muss "LLC" oder "Limited Liability Company" enthalten und 
                          einzigartig in Wyoming sein. Northwest prüft die Verfügbarkeit automatisch.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        2
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Bei Northwest anmelden
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Besuchen Sie Northwest Registered Agent, wählen Sie Wyoming als 
                          Gründungsstaat und geben Sie Ihre Unternehmensdaten ein.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        3
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Bezahlen & einreichen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Nach der Bezahlung reicht Northwest Ihre Articles of Organization beim 
                          Wyoming Secretary of State ein — typischerweise am selben Werktag.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        4
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          LLC ist aktiv (1-2 Tage)
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Wyoming bearbeitet Anträge schnell. Innerhalb von 1-2 Werktagen erhalten 
                          Sie Ihre genehmigten Dokumente im Northwest Dashboard.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        5
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          EIN beantragen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Beantragen Sie Ihre EIN (Steuernummer) bei der IRS. Northwest bietet 
                          diesen Service an, oder Sie können es über den EIN-Beantragungsprozess 
                          selbst tun (für Ausländer per Fax).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-highlight p-6 mt-8">
                  <h4 className="font-heading font-bold text-navy-900 mb-3">Bereit zu starten?</h4>
                  <p className="text-slate-700 text-sm mb-4">
                    Mit Northwest Registered Agent können Sie Ihre Wyoming LLC in weniger als 
                    15 Minuten online gründen — und das für nur $39 + Staatsgebühren.
                  </p>
                  <a
                    href="https://www.northwestregisteredagent.com"
                    target="_blank"
                    rel="nofollow sponsored noopener noreferrer"
                    className="cta-primary inline-flex"
                    data-testid="wyoming-cta"
                  >
                    Wyoming LLC mit Northwest gründen
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16">
                <h2>Häufig gestellte Fragen</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              <TableOfContents items={tocItems} />
              
              <CTABox
                variant="sidebar"
                highlights={[
                  'Wyoming: $100 Staatsgebühr',
                  'Northwest: $39 Gründungsservice',
                  '1 Jahr Registered Agent gratis',
                  'Gründung in 1-2 Werktagen'
                ]}
              />

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
                <h4 className="font-heading font-bold text-navy-900 mb-4">Verwandte Ratgeber</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/ratgeber/llc-delaware-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LLC in Delaware gründen
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/ein-nummer-beantragen" className="text-sm text-slate-700 hover:text-teal-600">
                      → EIN Nummer beantragen
                    </Link>
                  </li>
                  <li>
                    <Link to="/llc-usa-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LLC Gründung USA (Hauptguide)
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default LLCWyomingPage;
